import { Plus } from "lucide-react";
import { useState } from "react";

import { queryKeys } from "@/api/hooks";
import { isTerminalQueryError } from "@/api/queryClient";
import {
  useLibraryPartManifestQuery,
  type LibraryAsset,
} from "@/api/library";
import { useRetryPausedQuery } from "@/api/useRetryPausedQuery";
import { useCapability } from "@/auth/SessionContext";
import { lifecycleRefusal } from "@/lib/archiveRefusal";
import {
  addNamedRole,
  addableRoles,
  findNamedRow,
  manifestFor,
  moveNamedRow,
  namedRows,
  promptRowCount,
  removeNamedRow,
  updateNamedDocument,
} from "@/lib/namedParts";
import { PageHeader } from "@/shell/PageHeader";
import { Button } from "@/ui/Button";
import { Tooltip } from "@/ui/Tooltip";
import { SaveStatusLine } from "@/ui/library/AssetEditor";
import { LifecycleDialog, type LifecycleMode } from "@/ui/library/LifecycleDialog";
import { LoadingState, PartialDataNotice, UnavailableState } from "@/ui/states";

import { NamedAssetMeta } from "./NamedAssetChrome";
import { NamedReadFailureState } from "./NamedReadFailure";
import {
  NamedDocumentActions,
  NamedDocumentEditor,
  isAuthorableDocumentSet,
} from "./NamedDocumentEditor";
import {
  NamedConflictRecovery,
  NamedProblems,
  NamedRefusal,
} from "./NamedDraftStates";
import { NamedRowList } from "./NamedRowList";
import { NewMainConcernDialog } from "./NewMainConcernDialog";
import { useNamedDraft } from "./useNamedDraft";

/**
 * The canonical Main-concern detail (#492).
 *
 * Heading, prompt count, version and protection state, Details, "Add prompt",
 * and a list of named documents each opening its own editor — in place of the
 * generic `AssetEditor`/`PartsEditor` pair, whose free-text "Part key" input is
 * the manual-key escape hatch the 2026-08-25 amendment forbids on this family.
 *
 * WHAT THE CANONICAL HAS AND THIS DOES NOT, and why, so a reviewer does not have
 * to diff it against the mock to find out:
 *
 *   * **Rename.** There is no rename route. `/v1/library/assets/{assetId}` is
 *     GET-only, and archive/unarchive are the only lifecycle writes the contract
 *     declares. A Rename button would be a control with nothing behind it, which
 *     ADR 0001's no-dead-controls rule forbids and which #516 settled by deleting
 *     such surfaces rather than disabling them.
 *   * **An editable per-row description.** A row's name IS its role, and the role
 *     is service vocabulary; an editable label would be a part-key input in
 *     friendlier words. See `NamedRowList`.
 *
 * Both are recorded as canonical divergences for #448 rather than papered over.
 */
export function MainConcernAssetPage({ asset }: { asset: LibraryAsset }) {
  const state = useNamedDraft(asset);
  const manifest = useLibraryPartManifestQuery();
  const retryManifest = useRetryPausedQuery(queryKeys.libraryPartManifest);
  const write = useCapability("library.write");
  const restore = useCapability("library.restore");
  const [openRole, setOpenRole] = useState<string | null>(null);
  const [lifecycle, setLifecycle] = useState<LifecycleMode | null>(null);
  const [copying, setCopying] = useState(false);

  const breadcrumbs = [
    { label: "Clinical library", to: "/library" },
    { label: "Main concern", to: "/library/chief-complaint" },
  ];

  if (state.status === "loading") {
    return (
      <>
        <PageHeader breadcrumbs={breadcrumbs} title={asset.name} />
        <LoadingState label="Loading asset content" rows={4} />
      </>
    );
  }

  if (state.status === "error") {
    return (
      <>
        <PageHeader breadcrumbs={breadcrumbs} title={asset.name} />
        <NamedReadFailureState
          failure={state.failure}
          isPaused={state.isPaused}
          retry={state.retry}
          subject="Asset content"
          title="This asset's content could not be loaded"
          description={`The Editor Service did not return the content at ${asset.latestVersion}, so there is nothing to edit yet.`}
          deniedDescription="You are not authorized to read this asset's content."
          offlineDescription={`The content at ${asset.latestVersion} could not be reached, so there is nothing to edit yet. It loads on its own when the connection returns.`}
        />
      </>
    );
  }

  if (state.status === "unsupported") {
    return (
      <>
        <PageHeader breadcrumbs={breadcrumbs} title={asset.name} />
        <UnavailableState
          title="This asset's content is not a set of documents"
          description="A main concern is authored as named documents, and this asset's stored content is a different shape. It is shown by its own editor instead."
        />
      </>
    );
  }

  const { editor } = state;
  const rows = namedRows(editor.parts, "main_concern");
  const open = openRole === null ? null : findNamedRow(rows, openRole);
  const promptCount = promptRowCount(rows);

  const entry = manifestFor(manifest.data, "main_concern");
  const nextPrompt = addableRoles(entry, editor.parts, "prompt_text")[0] ?? null;

  // THE MANIFEST HAS THREE STATES THIS SURFACE CARES ABOUT, not two — the same
  // three `BackgroundSetSurface` draws beside it, for the same control.
  //
  // `isError` is the SETTLED half only. A retry held by the retryer keeps
  // `isPending: true` and `isError: false`, so a manifest read that will never
  // arrive on its own — a 503 the Editor Service really sent, parked because the
  // tab went to the background — reached the "Still reading" arm below and
  // stayed there: "Add prompt" refused as though a roster were seconds away, no
  // failure on screen, and nothing to retry. That is #327's misclassification on
  // the post-create journey this ticket exists to deliver, one surface over from
  // the creation dialog where it was already fixed.
  //
  // RETAINED DATA DOES NOT EXCUSE THE READ EITHER. `entry` survives a failed
  // REFRESH, so the roster on screen is the last one the service confirmed and
  // the browser cannot say whether it is still that. Adding from it is an
  // assertion about service policy made by a client whose last read of that
  // policy failed, so `manifestUnread` refuses regardless of what is cached —
  // the ruling "New set file" already applies to the same manifest.
  const manifestFailure = manifest.isPaused
    ? manifest.failureReason
    : manifest.isError
      ? manifest.error
      : null;
  const manifestUnread = manifest.isPaused || manifest.isError;

  const addRefusal =
    manifestUnread || (!manifest.isPending && entry === null)
      ? "The Editor Service did not report which documents this asset type allows."
      : manifest.isPending
        ? "Still reading which documents this asset type allows."
        : nextPrompt === null
          ? "Every prompt this asset type allows is already here."
          : null;

  const archiveMode: LifecycleMode = asset.capabilities.archived
    ? "unarchive"
    : "archive";
  // Direction-specific caller gate (#647): archiving is `library.write`,
  // restoring is the admin/operations `library.restore` the ratified contract
  // (§2.1) reserves — the same split the service enforces on the two routes.
  const archiveRefusal = lifecycleRefusal(
    asset,
    archiveMode === "unarchive" ? restore.reason : write.reason,
  );
  const archiveLabel = archiveMode === "archive" ? "Archive" : "Unarchive";

  // The row editor is a screen of its own in the canonical reference, reached by
  // Open and returned from by Back. It replaces the list rather than sitting
  // beside it, and its breadcrumb carries the asset back to the list.
  //
  // A row whose documents are engine-owned — `steps`, `schema`, `diagnosis` —
  // reaches this screen and its chrome, and `NamedDocumentEditor` states why it
  // cannot be authored. Save is withheld there rather than disabled: nothing on
  // that screen can ever make a draft dirty, so a Save button would be a control
  // with no path to being usable.
  if (open) {
    const authorable = isAuthorableDocumentSet(open.documents);
    return (
      <>
        <PageHeader
          breadcrumbs={[
            ...breadcrumbs,
            { label: asset.name, to: `/library/chief-complaint/${asset.id}` },
            { label: open.label },
          ]}
          title={open.label}
          description={authorable ? "Row prompt" : "Clinical engine document"}
          meta={
            <NamedAssetMeta
              asset={asset}
              version={editor.pinned}
              identity={`chief-complaint/${asset.id}`}
            />
          }
          actions={
            <NamedDocumentActions
              readOnly={editor.readOnly || !authorable}
              dirty={editor.dirty}
              saving={editor.saving}
              onBack={() => setOpenRole(null)}
              onSave={() => editor.save()}
            />
          }
        />
        <NamedConflictRecovery editor={editor} />
        <NamedDocumentEditor
          documents={open.documents}
          readOnly={editor.readOnly}
          readOnlyReason={editor.readOnlyReason}
          problems={editor.problems}
          onChange={(documentId, text) =>
            editor.change(updateNamedDocument(editor.parts, documentId, text))
          }
        />
        <div className="mt-4">
          <NamedRefusal editor={editor} />
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        breadcrumbs={breadcrumbs}
        title={asset.name}
        description={`${promptCount} ${promptCount === 1 ? "prompt" : "prompts"}`}
        meta={
          <NamedAssetMeta
            asset={asset}
            version={editor.pinned}
            identity={`chief-complaint/${asset.id}`}
          />
        }
        actions={
          <>
            {asset.capabilities.seeded ? (
              <Tooltip content="Part of the base clinical library. The original stays as it is — make an editable copy to create your own version.">
                <Button variant="outline" onClick={() => setCopying(true)}>
                  Create editable copy
                </Button>
              </Tooltip>
            ) : null}
            <Tooltip content={archiveRefusal ?? `${archiveLabel} this asset`}>
              <Button
                variant="outline"
                disabled={archiveRefusal !== null}
                onClick={() => setLifecycle(archiveMode)}
              >
                {archiveLabel}
              </Button>
            </Tooltip>
            {editor.readOnly ? null : (
              <Tooltip content={addRefusal ?? `Add ${nextPrompt?.role ?? "a prompt"}`}>
                <Button
                  disabled={addRefusal !== null}
                  onClick={() => {
                    if (nextPrompt) {
                      editor.change(
                        addNamedRole(
                          editor.parts,
                          nextPrompt.role,
                          nextPrompt.kind as "prompt_text" | "structured",
                          nextDraftId,
                        ),
                      );
                    }
                  }}
                >
                  <Plus aria-hidden="true" className="h-4 w-4" />
                  Add prompt
                </Button>
              </Tooltip>
            )}
          </>
        }
      />

      <div className="space-y-4">
        {/* A REFUSAL IN A TOOLTIP IS NOT A REPORTED FAILURE. The tooltip on the
            disabled control says why THAT control is refused, to someone who
            hovers it; the read that failed is a fact about this page, and it
            needs somewhere it is simply visible — with the recovery, since the
            control that would carry one is disabled.

            A RETRY IS OFFERED ONLY WHERE IT COULD RUN: a 401, 403, 404 or 501
            has settled and asking again returns the same answer, so those
            failures get the notice without a control (#4's rule, and the line
            `QueryErrorState` already draws). Everything that can reach the
            paused state is retryable by construction — `queryClient`'s `retry`
            rejects a terminal status before the retryer ever sleeps — and the
            recovery is cancel-then-refetch, the only kind that clears a held
            read. */}
        <PartialDataNotice
          visible={manifestUnread}
          description="The Editor Service did not report which documents this asset type allows, so no prompt can be added until that read succeeds. Everything else on this page is unaffected."
          {...(manifestUnread && !isTerminalQueryError(manifestFailure)
            ? {
                action: (
                  <Button variant="outline" onClick={retryManifest}>
                    Try again
                  </Button>
                ),
              }
            : {})}
        />

        <NamedConflictRecovery editor={editor} />
        <NamedRefusal editor={editor} />
        <NamedProblems editor={editor} />

        <NamedRowList
          rows={rows}
          readOnly={editor.readOnly}
          onOpen={setOpenRole}
          onMove={(role, to) => editor.change(moveNamedRow(editor.parts, role, to))}
          onRemove={(role) => editor.change(removeNamedRow(editor.parts, role))}
          // No add control here: the canonical Main-concern detail puts
          // "Add prompt" beside the heading, and it is in this page's header
          // actions above. A second one would be the same button twice.
        />

        {editor.readOnly ? null : (
          <div className="flex flex-wrap items-center justify-end gap-3">
            <SaveStatusLine
              status={
                editor.saving
                  ? "saving"
                  : editor.failure !== null
                    ? "error"
                    : editor.dirty
                      ? "dirty"
                      : editor.lastSavedAt
                        ? "saved"
                        : "clean"
              }
              lastSavedAt={editor.lastSavedAt}
            />
            <Button
              variant="outline"
              disabled={!editor.dirty || editor.saving}
              onClick={editor.discard}
            >
              Discard
            </Button>
            <Button
              disabled={!editor.dirty || editor.saving}
              onClick={() => editor.save()}
            >
              Save
            </Button>
          </div>
        )}
      </div>

      {lifecycle ? (
        <LifecycleDialog
          asset={asset}
          mode={lifecycle}
          onClose={() => setLifecycle(null)}
        />
      ) : null}
      {copying ? (
        <NewMainConcernDialog
          copyFrom={asset}
          onClose={() => setCopying(false)}
        />
      ) : null}
    </>
  );
}

/**
 * Identity for a newly added document, distinct from its key.
 *
 * The same reason `assetContent.newDraftPart` keeps one: two keyless-equal
 * documents mid-edit would make React reuse the wrong row's DOM, including the
 * focused control.
 */
let addedDocuments = 0;
function nextDraftId(): string {
  addedDocuments += 1;
  return `named-document-${addedDocuments}`;
}
